import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  learnerCoursesService,
  RecordResourceProgressPayload,
  CompleteModulePayload,
  LearnerEnrollmentsFilters,
} from '@/services/learnerCoursesService'
import { LearnerCourseProgressModel, LearnerProgrammeEnrollmentModel } from '@/models/learnerCourse.model'
import { ModuleProgressModel, ResourceProgressModel } from '@/models/course.model'

export const learnerCourseKeys = {
  root: () => ['learner-courses'] as const,
  enrollments: (filters?: LearnerEnrollmentsFilters) =>
    [...learnerCourseKeys.root(), 'enrollments', filters ?? {}] as const,
  progressRoot: () => [...learnerCourseKeys.root(), 'progress'] as const,
  progress: (courseId: string) => [...learnerCourseKeys.progressRoot(), courseId] as const,
}

const useNormalizedEnabled = (enabled: boolean | undefined, courseId?: string) => {
  return useMemo(() => Boolean(enabled ?? true) && Boolean(courseId), [enabled, courseId])
}

const normalizeFilters = (filters?: LearnerEnrollmentsFilters): LearnerEnrollmentsFilters => {
  const normalized: LearnerEnrollmentsFilters = {}
  if (filters?.level !== undefined) normalized.level = filters.level
  if (filters?.stage !== undefined) normalized.stage = filters.stage
  if (filters?.page !== undefined) normalized.page = filters.page
  if (filters?.perPage !== undefined) normalized.perPage = filters.perPage
  return normalized
}

export function useLearnerEnrollments(filters?: LearnerEnrollmentsFilters) {
  const normalizedFilters = normalizeFilters(filters)

  return useQuery({
    queryKey: learnerCourseKeys.enrollments(normalizedFilters),
    queryFn: async (): Promise<LearnerProgrammeEnrollmentModel[]> => {
      return learnerCoursesService.getLearnerEnrollments(normalizedFilters)
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export function useLearnerCourseProgress(courseId: string | undefined, options?: { enabled?: boolean }) {
  const enabled = useNormalizedEnabled(options?.enabled, courseId)

  return useQuery({
    queryKey: courseId ? learnerCourseKeys.progress(courseId) : learnerCourseKeys.progressRoot(),
    queryFn: async (): Promise<LearnerCourseProgressModel> => {
      if (!courseId) {
        throw new Error('Course ID is required')
      }
      return learnerCoursesService.getCourseProgress(courseId)
    },
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useRecordResourceProgress() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: {
      courseId: string
      resourceId: string
      payload: RecordResourceProgressPayload
    }): Promise<ResourceProgressModel> => {
      const { courseId, resourceId, payload } = variables
      return learnerCoursesService.recordResourceProgress(courseId, resourceId, payload)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: learnerCourseKeys.progress(variables.courseId) })
    },
  })
}

export function useCompleteCourseModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: {
      courseId: string
      moduleId: string
      payload: CompleteModulePayload
    }): Promise<ModuleProgressModel> => {
      const { courseId, moduleId, payload } = variables
      return learnerCoursesService.completeModule(courseId, moduleId, payload)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: learnerCourseKeys.progress(variables.courseId) })
    },
  })
}
